<!-- eslint-disable vue/no-bare-strings-in-template @intlify/vue-i18n/no-raw-text vue/custom-event-name-casing -->
<script setup>
import { computed, ref, onMounted } from 'vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisConfirmModal from 'dashboard/components-next/LaisConfirmModal.vue';

const props = defineProps({
  imovel: { type: Object, required: true },
  agent: { type: Object, default: null },
  visitHistory: { type: Array, default: () => [] },
  suggestions: { type: Array, default: () => [] },
  openOffer: { type: Object, default: null },
  source: { type: String, default: 'hero_button' },
  isLoading: { type: Boolean, default: false },
  errorMessage: { type: String, default: '' },
  keyHolderBlock: { type: Object, default: null },
});

const emit = defineEmits([
  'schedule-visit',
  'register-visit',
  'contact-agent',
  'contact-owner',
  'contact-lr',
  'open-offer',
  'refresh',
  'tab-change',
  'opened',
  'open-key-holder',
]);

const TABS = [
  { id: 'overview', label: 'Visão geral' },
  { id: 'entry-info', label: 'Acesso ao imóvel' },
  { id: 'suggestions', label: 'Sugestões' },
  { id: 'visit-history', label: 'Histórico de visitas' },
];

const activeTab = ref('overview');
const showOptOutModal = ref(false);

onMounted(() => {
  emit('opened', { source: props.source });
});

const setTab = id => {
  activeTab.value = id;
  emit('tab-change', { tab: id, source: props.source });
};

const entryAccess = computed(() => props.imovel?.entry_access || {});
const accessKind = computed(() => entryAccess.value.kind || 'pending');
const keysWithAgent = computed(() =>
  Boolean(entryAccess.value.keys_with_agent)
);
const canOpenKeyHolderBlock = computed(
  () =>
    Boolean(props.keyHolderBlock?.blocked) &&
    props.keyHolderBlock?.validationEnabled !== false
);
const entryDetails = computed(() =>
  Array.isArray(entryAccess.value.details) ? entryAccess.value.details : []
);
const entryHelper = computed(
  () => entryAccess.value.helper_info || entryAccess.value.helper || ''
);
const entryConditions = computed(
  () =>
    entryAccess.value.conditions ||
    entryAccess.value.instructions ||
    entryAccess.value.metadata?.conditions ||
    ''
);
const entryAssignee = computed(() => entryAccess.value.assignee || {});

const accessKindLabels = {
  portaria: 'Chaves na portaria',
  password: 'Entrada com senha',
  lockbox: 'Caixinha porta-chaves Imudei (Lockbox)',
  locker: 'Chaves no escritório Imudei (HUB)',
  chave_com_corretor: 'Chave com corretor',
  chave_com_parceiro: 'Chaves na imobiliária parceira',
  sindico_zelador: 'Chaves com outro responsável',
  chave_com_proprietario: 'Chaves com o proprietário',
  chave_com_inquilino: 'Chaves com o atual inquilino',
  pending: 'Em definição',
};

const accessLabel = computed(
  () =>
    entryAccess.value.access_label ||
    accessKindLabels[accessKind.value] ||
    'Em definição'
);

const triggerOptOut = () => {
  showOptOutModal.value = true;
};

const confirmOptOut = () => {
  showOptOutModal.value = false;
  emit('refresh', { reason: 'keys_with_agent_opt_out' });
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template @intlify/vue-i18n/no-raw-text -->
  <LaisCard id="accordionagent_area-header" title="Área do corretor">
    <div
      v-if="isLoading"
      class="mb-4 flex items-center gap-3 rounded-lais bg-lais-bg-soft px-4 py-3 text-[13px] text-lais-text-muted"
    >
      <span
        class="i-lucide-loader-circle size-4 animate-spin"
        aria-hidden="true"
      />
      Carregando área do corretor…
    </div>
    <div
      v-else-if="errorMessage"
      class="mb-4 rounded-lais border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-700"
    >
      {{ errorMessage }}
    </div>

    <div class="flex flex-wrap gap-2 mb-4 overflow-x-auto no-scrollbar">
      <button
        v-for="tab in TABS"
        :key="tab.id"
        type="button"
        class="rounded-full border px-4 py-1.5 text-[13px] font-semibold whitespace-nowrap"
        :class="
          activeTab === tab.id
            ? 'border-lais-primary bg-lais-primary text-white'
            : 'border-lais-border bg-white text-lais-text'
        "
        @click="setTab(tab.id)"
      >
        {{ tab.label }}
      </button>
    </div>

    <section v-if="activeTab === 'overview'" class="grid gap-3 text-[13px]">
      <p>
        <strong>Corretor responsável:</strong>
        {{ agent?.name || 'Aguardando atribuição' }}
      </p>
      <p v-if="agent?.code">
        Código: <code>{{ agent.code }}</code>
      </p>
      <div class="flex flex-wrap gap-2">
        <LaisPrimaryButton
          icon="i-lucide-calendar-plus"
          @click="emit('schedule-visit')"
        >
          Agendar visita
        </LaisPrimaryButton>
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-clipboard-check"
          @click="emit('register-visit')"
        >
          Registrar visita
        </LaisPrimaryButton>
        <LaisPrimaryButton
          v-if="agent?.contact_url"
          variant="ghost"
          icon="i-lucide-phone"
          @click="emit('contact-agent', agent)"
        >
          Falar com corretor
        </LaisPrimaryButton>
        <LaisPrimaryButton
          v-if="imovel?.owner_contact_url"
          variant="ghost"
          icon="i-lucide-user"
          @click="emit('contact-owner')"
        >
          Falar com proprietário
        </LaisPrimaryButton>
        <LaisPrimaryButton
          v-if="imovel?.lr_contact_url"
          variant="ghost"
          icon="i-lucide-headset"
          @click="emit('contact-lr')"
        >
          Falar com LR
        </LaisPrimaryButton>
      </div>
      <div
        v-if="openOffer"
        class="rounded-lais bg-emerald-50 px-3 py-2 text-[12px] text-emerald-800"
      >
        Proposta aberta em andamento (#{{
          openOffer.booking_reference || openOffer.token
        }}).
        <button
          type="button"
          class="underline ml-1"
          @click="emit('open-offer', openOffer)"
        >
          Abrir proposta
        </button>
      </div>
    </section>

    <section v-if="activeTab === 'entry-info'" class="grid gap-3 text-[13px]">
      <div
        class="rounded-lais border border-lais-border bg-white px-4 py-3 text-[13px] leading-5"
      >
        <p class="text-[11px] font-semibold uppercase text-lais-text-muted">
          Tipo de acesso
        </p>
        <p class="mt-1 text-[15px] font-semibold text-lais-text">
          {{ accessLabel }}
        </p>
        <p v-if="entryHelper" class="mt-1 text-lais-text-muted">
          {{ entryHelper }}
        </p>
      </div>
      <div
        v-if="canOpenKeyHolderBlock"
        class="rounded-lais border border-amber-200 bg-amber-50 px-4 py-3 text-[12px] leading-5 text-amber-900"
      >
        <p class="font-semibold">O imóvel já possui um corretor responsável</p>
        <p>
          {{ keyHolderBlock.holderName || 'Outro responsável' }} está com a
          chave deste imóvel.
        </p>
        <button
          type="button"
          class="mt-1 font-semibold underline"
          @click="
            emit('open-key-holder', {
              role: keyHolderBlock.role,
              source: 'agent_area_entry_info',
            })
          "
        >
          Ver responsável
        </button>
      </div>
      <p v-if="entryConditions">
        <strong>Condições:</strong> {{ entryConditions }}
      </p>
      <dl v-if="entryDetails.length" class="grid gap-2 sm:grid-cols-2">
        <div
          v-for="detail in entryDetails"
          :key="detail.key"
          class="rounded-lais-sm bg-lais-bg-soft px-3 py-2"
        >
          <dt class="text-[11px] font-semibold uppercase text-lais-text-muted">
            {{ detail.label }}
          </dt>
          <dd class="mt-1 font-semibold text-lais-text">
            {{ detail.value }}
          </dd>
          <p v-if="detail.helper" class="mt-1 text-[12px] text-lais-text-muted">
            {{ detail.helper }}
          </p>
        </div>
      </dl>
      <div
        v-else-if="
          entryAssignee.name || entryAssignee.phone || entryAssignee.email
        "
        class="rounded-lais-sm bg-lais-bg-soft px-3 py-2"
      >
        <p class="text-[11px] font-semibold uppercase text-lais-text-muted">
          Dados do responsável
        </p>
        <p v-if="entryAssignee.name" class="mt-1 font-semibold text-lais-text">
          {{ entryAssignee.name }}
        </p>
        <p v-if="entryAssignee.phone" class="text-lais-text-muted">
          {{ entryAssignee.phone }}
        </p>
        <p v-if="entryAssignee.email" class="break-all text-lais-text-muted">
          {{ entryAssignee.email }}
        </p>
      </div>
      <div class="flex flex-wrap gap-2">
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-key"
          @click="emit('refresh', { reason: 'keys_with_agent_opt_in' })"
        >
          {{ keysWithAgent ? 'Chaves comigo' : 'Quero ficar com as chaves' }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          v-if="keysWithAgent"
          variant="ghost"
          icon="i-lucide-x"
          class="text-rose-700"
          @click="triggerOptOut"
        >
          Devolver as chaves
        </LaisPrimaryButton>
      </div>
    </section>

    <section v-if="activeTab === 'suggestions'" class="grid gap-3 text-[13px]">
      <div v-if="!suggestions.length" class="text-lais-text-muted">
        Nenhuma sugestão operacional agora.
        <button
          type="button"
          class="underline"
          @click="emit('refresh', { reason: 'suggestions_refresh' })"
        >
          Tentar novamente
        </button>
      </div>
      <ul v-else class="grid gap-2">
        <li
          v-for="suggestion in suggestions"
          :key="suggestion.id"
          class="rounded-lais border border-lais-border bg-white p-3"
        >
          <p class="font-semibold">{{ suggestion.title }}</p>
          <p class="text-lais-text-muted">{{ suggestion.body }}</p>
        </li>
      </ul>
    </section>

    <section
      v-if="activeTab === 'visit-history'"
      class="grid gap-3 text-[13px]"
    >
      <div v-if="!visitHistory.length" class="text-lais-text-muted">
        Nenhuma visita registrada para este imóvel.
      </div>
      <ul v-else class="grid gap-2">
        <li
          v-for="visit in visitHistory"
          :key="visit.id"
          class="rounded-lais border border-lais-border bg-white p-3"
        >
          <p class="font-semibold">
            {{ visit.scheduled_date }} · {{ visit.scheduled_hour_from }}
          </p>
          <p class="text-lais-text-muted">
            {{ visit.contact_name || 'Cliente anônimo' }} ·
            {{ visit.status_label || visit.status }}
          </p>
        </li>
      </ul>
    </section>

    <LaisConfirmModal
      :show="showOptOutModal"
      title="Devolver chaves para o proprietário?"
      description="Você não poderá mais agendar visitas com chave-com-corretor neste imóvel até reabilitar a opção."
      confirm-text="Confirmar"
      @close="showOptOutModal = false"
      @confirm="confirmOptOut"
    />
  </LaisCard>
</template>
